import Link from "next/link";
import { cn } from "./cn";

type PagerProps = {
  currentPage: number;
  lastPage: number;
  basePath: string;
  searchParams?: Record<string, string | undefined>;
  labels?: { previous?: string; next?: string };
};

function pageHref(
  basePath: string,
  searchParams: Record<string, string | undefined> | undefined,
  page: number,
): string {
  const params = new URLSearchParams();
  for (const [k, v] of Object.entries(searchParams ?? {})) {
    if (v && k !== "page") params.set(k, v);
  }
  params.set("page", String(page));
  return `${basePath}?${params.toString()}`;
}

/**
 * Windowed pager over ?page links — 1 … c−1 c c+1 … max, category filter
 * preserved. Styled to the media.n24.si reference (design teardown 2026-08-31):
 * h-8 rounded-md outline buttons that fill on hover, the current page solid in
 * the page ink (the reference's gray-900 — deliberately NOT the brand color,
 * so the pager stays quiet next to teal chips and links), 40px minimum on the
 * numbers so single digits don't wobble the row.
 */
const btn =
  "inline-flex h-8 items-center justify-center rounded-md border border-light-gray bg-card px-3 text-sm font-medium text-foreground no-underline transition-colors hover:bg-muted";

export function Pager({ currentPage, lastPage, basePath, searchParams, labels }: PagerProps) {
  if (lastPage <= 1) return null;
  const prev = labels?.previous ?? "← Prev";
  const next = labels?.next ?? "Next →";
  const pages: (number | "…")[] = [];
  const max = lastPage;
  const c = currentPage;
  const push = (n: number | "…") => pages.push(n);
  push(1);
  if (c - 2 > 2) push("…");
  for (let i = Math.max(2, c - 1); i <= Math.min(max - 1, c + 1); i++) push(i);
  if (c + 2 < max - 1) push("…");
  if (max > 1) push(max);

  return (
    <nav className="mt-10 flex flex-wrap items-center justify-center gap-2" aria-label="Pagination">
      {/* Disabled ends render as spans — pointer-events-none on a Link only
          blocks the mouse; keyboard users could still tab to and "activate" a
          dead link. */}
      {currentPage === 1 ? (
        <span className={cn(btn, "opacity-50")}>{prev}</span>
      ) : (
        <Link href={pageHref(basePath, searchParams, currentPage - 1)} className={btn}>
          {prev}
        </Link>
      )}
      {pages.map((p, i) =>
        p === "…" ? (
          <span key={`e-${i}`} className="text-medium-gray px-2 text-sm">
            …
          </span>
        ) : (
          <Link
            key={p}
            href={pageHref(basePath, searchParams, p)}
            aria-current={p === currentPage ? "page" : undefined}
            className={cn(
              btn,
              "min-w-[40px] tabular-nums",
              p === currentPage &&
                "border-foreground bg-foreground text-background hover:bg-foreground",
            )}
          >
            {p}
          </Link>
        ),
      )}
      {currentPage === lastPage ? (
        <span className={cn(btn, "opacity-50")}>{next}</span>
      ) : (
        <Link href={pageHref(basePath, searchParams, currentPage + 1)} className={btn}>
          {next}
        </Link>
      )}
    </nav>
  );
}
